import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Checkbox, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Stack, Text, useToast,
} from '@chakra-ui/react';
import { useRnwySystems, useRnwyLink } from '../hooks/useRnwyStatus';

interface RnwyLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  currentSystemId?: string | null; // item 1124 — CSV when more than one processor is linked
}

// item 861 — admin-only: link a Basix project to its deployed RNWY system(s) (the status
// API's projectId slugs). The picker is populated LIVE from the rnwy-system list, so
// there's no guessing/mapping by name — link once, every capacity surface lights up.
// item 1124 (Chad) — a property can run MORE THAN ONE processor: the single dropdown is now
// a checkbox list; the selection saves as a CSV on the project.
const splitCsv = (csv?: string | null): string[] => (csv ?? '')
  .split(',').map((s) => s.trim()).filter((s) => s.length > 0);

const RnwyLinkModal: React.FC<RnwyLinkModalProps> = ({
  isOpen, onClose, projectId, projectName, currentSystemId,
}) => {
  const toast = useToast();
  const { data, isLoading } = useRnwySystems(isOpen);
  const link = useRnwyLink();
  const [selected, setSelected] = useState<string[]>(splitCsv(currentSystemId));

  useEffect(() => {
    if (isOpen) setSelected(splitCsv(currentSystemId));
  }, [isOpen, currentSystemId]);

  // a linked slug missing from the live list (status API hiccup, renamed box) must stay
  // visible — otherwise opening the modal would silently drop it from the selection
  const options = useMemo(() => {
    const live = data?.systems ?? [];
    const extras = splitCsv(currentSystemId).filter((s) => !live.includes(s));
    return [...extras, ...live];
  }, [data, currentSystemId]);

  const toggle = (s: string) => setSelected(
    (prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]),
  );

  const save = async (rnwySystemId: string | null) => {
    try {
      await link.mutateAsync({ projectId, rnwySystemId });
      toast({ status: 'success', title: rnwySystemId ? 'RNWY system links saved.' : 'RNWY systems unlinked.' });
      onClose();
    } catch {
      toast({ status: 'error', title: 'Could not save the RNWY link.' });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Link RNWY systems</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" opacity={0.75} mb={4}>
            {`Tie “${projectName}” to its deployed RNWY system(s) so live venue capacity shows on the project. Check every processor running at this property.`}
          </Text>
          {isLoading && <Text fontSize="sm" opacity={0.6}>Loading systems…</Text>}
          {!isLoading && (
            <Stack spacing={1.5} maxH="260px" overflowY="auto" pr={2}>
              {options.map((s) => (
                <Checkbox key={s} isChecked={selected.includes(s)} onChange={() => toggle(s)}>
                  <Text as="span" fontSize="sm">{s}</Text>
                </Checkbox>
              ))}
              {options.length === 0 && <Text fontSize="sm" opacity={0.6}>No deployed systems reported.</Text>}
            </Stack>
          )}
          {data?.enabled === false && (
            <Text fontSize="xs" color="orange.300" mt={2}>
              The RNWY status API keys aren’t configured on this environment yet.
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          {splitCsv(currentSystemId).length > 0 && (
            <Button variant="ghost" colorScheme="red" mr="auto" isLoading={link.isLoading} onClick={() => save(null)}>
              Unlink all
            </Button>
          )}
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button
            colorScheme="brandGreen"
            isDisabled={selected.length === 0}
            isLoading={link.isLoading}
            onClick={() => save(selected.join(','))}
          >
            {`Link ${selected.length > 1 ? `${selected.length} systems` : 'system'}`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

RnwyLinkModal.defaultProps = { currentSystemId: null };

export default RnwyLinkModal;
